/**
 * Overview - port from `1116/omega-club/dashboard/src/components/Overview.jsx`.
 * Reads from `useTenantData(slug)`.
 *
 * Replaces the CHAP-78 stub Overview with the real port. Per CHAP-79
 * (Phase 1C #45).
 *
 * Parity notes vs omega-club:
 * - omega-club's hardcoded hero includes fixed project specs (square footage,
 *   stories, beds, parking) and headline financials (total cost, capital
 *   required, mortgage, 25-yr value). These come from
 *   `projectSummary` which lives outside the chapter-build TenantConfig.
 *   The port derives sqft + beds from `llcEntities` (sums), and computes
 *   total cost from `capitalStack`. Story count + parking + Capital
 *   Required + 25-Yr Value need additional schema fields (separate ticket).
 * - The "Eric Handler" quote is omega-zeta specific and stays hardcoded
 *   for now; tenant-level inspirational quote field is Phase 2.
 * - Quick Stats Row's "Rent vs Market" comes from properties data not in
 *   TenantConfig; omitted until properties schema lands.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import { useTenantData } from '../hooks/useTenantData.js';
import { DecisionGateCard } from '../components/DecisionGateCard.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { formatCurrency, formatCurrencyFull, formatNumber } from '../lib/formatters.js';

const RADIAN = Math.PI / 180;
function renderCustomLabel(props: PieLabelRenderProps) {
  // recharts 3: PieLabelRenderProps types midAngle + percent as number | undefined,
  // and cx/cy/radii can arrive as string | number via the SVG-prop intersection.
  const cx = Number(props.cx ?? 0);
  const cy = Number(props.cy ?? 0);
  const innerRadius = Number(props.innerRadius ?? 0);
  const outerRadius = Number(props.outerRadius ?? 0);
  const midAngle = props.midAngle ?? 0;
  const percent = props.percent ?? 0;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if (percent < 0.05) return null;
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

export default function Overview() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug ?? '');

  const totals = useMemo(() => {
    if (!data) return null;
    const totalCapital = data.capitalStack.reduce((s, e) => s + (e.value ?? 0), 0);
    const totalSqft = data.llcEntities.reduce((s, e) => s + (e.sqft ?? 0), 0);
    const totalBeds = data.llcEntities.reduce((s, e) => s + (e.beds ?? 0), 0);
    const totalRevenue = data.llcEntities.reduce((s, e) => s + (e.revenueYr1 ?? 0), 0);
    const totalNoi = data.llcEntities.reduce((s, e) => s + (e.noiYr1 ?? 0), 0);
    return { totalCapital, totalSqft, totalBeds, totalRevenue, totalNoi };
  }, [data]);

  // Hero tile 3: parking spaces when the tenant has parking data seeded
  // (feature_flags.parking_enabled=true AND a parking_revenue row), else
  // fall back to LLC count so the tile never shows a fabricated number.
  const parkingSpaces = data?.parking?.proposed?.spaces ?? null;
  const llcCount = data?.llcEntities.length ?? 0;

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;
  if (!data || !totals) return <PageState label="No data" />;

  return (
    <div className="space-y-3 sm:space-y-4 min-w-0">
      {/* Decision gate countdown (CHAP-249) — renders only if a gate is seeded */}
      <DecisionGateCard slug={slug ?? ''} />

      {/* Hero */}
      <div className="rounded-xl p-3 sm:p-5 lg:px-6 lg:py-5 text-white" style={{ backgroundColor: '#03244d' }}>
        <div className="flex items-start justify-between gap-4 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-orange text-lg sm:text-2xl font-bold">{data.tenant.displayName}</h2>
              <span className="text-xs bg-orange/20 text-orange px-2 py-0.5 rounded-full font-medium">Chapter House Rebuild</span>
              <span className="text-xs bg-orange text-white px-2 py-0.5 rounded-full font-semibold">{statusLabel(data.tenant.status)}</span>
            </div>
            {data.tenant.address && (
              <p className="text-neutral text-xs sm:text-sm mt-1">{data.tenant.address}</p>
            )}
            {(data.tenant.chapterOrg || data.tenant.university) && (
              <p className="text-neutral/70 text-xs mt-0.5">
                {[data.tenant.chapterOrg, data.tenant.university].filter(Boolean).join(' · ')}
              </p>
            )}
            {/* LLC count only shown here when it isn't already the tile-3
                fallback below (no seeded parking data) — avoids showing
                the same figure twice in the hero. */}
            {parkingSpaces != null && (
              <p className="text-neutral/60 text-xs mt-0.5">{llcCount} LLCs</p>
            )}
          </div>
        </div>
        {/* Project specs, not money — per Jake 2026-05-27 (matched here in
            chapter-build per Jake's approval): the capital basis is still
            undecided, so the hero is the wrong place for it, and the old
            "Total Capital" tile rendered $43.1M (HISTORICAL-SCENARIO - naming
            the retired figure is the whole point of this note), which is on
            figure-sheet-v2.json's own legacyBlacklist. Sqft/beds mirror
            omega-club's SpecChip data; parking falls back to LLC count
            when the tenant has no parking_revenue row seeded. Completion
            date has no tenant-level field yet, so "Fall 2028" is
            hardcoded here to match legacy's hardcoded value; move to
            tenant data when that field lands. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mt-3 pt-3 border-t border-white/10">
          <HeroStat label="Square Feet" value={formatNumber(totals.totalSqft)} />
          <HeroStat label="Beds"        value={String(totals.totalBeds)} />
          <HeroStat
            label={parkingSpaces != null ? 'Parking' : 'LLCs'}
            value={parkingSpaces != null ? formatNumber(parkingSpaces) : String(llcCount)}
          />
          <HeroStat label="Completion" value="Fall 2028" />
        </div>
      </div>

      {/* Capital Stack + LLC Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 sm:gap-4">
        {/* Capital Stack pie */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5">
          <h3 className="text-navy font-semibold text-xs sm:text-base mb-2">Capital Stack</h3>
          {data.capitalStack.length > 0 ? (
            <>
              <div className="h-44 sm:h-52 lg:h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.capitalStack.map((e) => ({ name: e.name, value: e.value ?? 0, color: e.color ?? '#94a3b8' }))}
                      cx="50%"
                      cy="50%"
                      innerRadius="50%"
                      outerRadius="85%"
                      paddingAngle={2}
                      dataKey="value"
                      labelLine={false}
                      label={renderCustomLabel}
                    >
                      {data.capitalStack.map((e, i) => (
                        <Cell key={`cell-${i}`} fill={e.color ?? '#94a3b8'} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrencyFull(value as number)}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1.5">
                {data.capitalStack.map((source) => {
                  const pct = totals.totalCapital > 0 ? (((source.value ?? 0) / totals.totalCapital) * 100).toFixed(0) : 'n/a';
                  return (
                    <div key={source.id} className="flex items-center justify-between gap-2 min-w-0" title={source.description ?? undefined}>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: source.color ?? '#94a3b8' }} />
                        <span className="text-xs sm:text-sm text-navy truncate">{source.name}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-xs sm:text-sm font-bold text-navy">{formatCurrency(source.value)}</span>
                        <span className="text-xs text-neutral">{pct}%</span>
                      </div>
                    </div>
                  );
                })}
                <div className="pt-1.5 mt-1.5 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs sm:text-sm font-semibold text-navy">Total <span className="text-neutral font-normal">= project target</span></span>
                  <span className="text-xs sm:text-base font-bold text-navy">{formatCurrencyFull(totals.totalCapital)}</span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-xs text-neutral italic py-8 text-center">No capital stack seeded.</p>
          )}
        </div>

        {/* LLC Cards */}
        <div className="lg:col-span-3 flex flex-col gap-2 sm:gap-3">
          <h3 className="text-navy font-semibold text-xs sm:text-base">LLC Structure</h3>
          {data.llcEntities.length === 0 ? (
            <p className="text-xs text-neutral italic">No LLCs seeded.</p>
          ) : (
            data.llcEntities
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((llc) => (
                <div key={llc.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-2.5 sm:p-4 min-w-0" title={llc.description ?? undefined}>
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2 min-w-0 flex-wrap gap-1">
                    <div className="min-w-0">
                      <h4 className="text-navy font-bold text-sm sm:text-base truncate">{llc.name}</h4>
                      {llc.subtitle && <p className="text-orange text-xs">{llc.subtitle}</p>}
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      {(llc.beds ?? 0) > 0 && (
                        <span className="text-xs bg-navy-dark text-orange px-2 py-0.5 rounded-full font-medium">{llc.beds} beds</span>
                      )}
                      {llc.sqft != null && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{formatNumber(llc.sqft)} sf</span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 pt-2 border-t border-gray-100">
                    <Mini label="Cost"        value={formatCurrency(llc.cost)} />
                    <Mini label="Revenue"     value={formatCurrency(llc.revenueYr1)} />
                    <Mini label="NOI"         value={formatCurrency(llc.noiYr1)} positive />
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* KPI quick view */}
      {data.kpis.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-5">
          <div className="flex items-center justify-between mb-3 min-w-0 flex-wrap gap-2">
            <h3 className="text-navy font-semibold text-xs sm:text-base">Quick KPIs</h3>
            <span className="text-xs text-neutral">See KPIs tab for full breakdown</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            {data.kpis.slice(0, 4).map((kpi) => (
              <div key={kpi.id} className="min-w-0">
                <p className="text-neutral text-xs uppercase tracking-wide truncate">{kpi.label}</p>
                <p className="text-navy text-base sm:text-lg font-bold mt-0.5 truncate">{kpi.valueString}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-neutral text-xs uppercase tracking-wider">{label}</p>
      <p className="text-white text-base sm:text-xl font-bold truncate">{value}</p>
    </div>
  );
}

function Mini({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-neutral text-xs uppercase tracking-wider">{label}</p>
      <p className={`font-bold text-sm sm:text-base truncate ${positive ? 'text-positive' : 'text-navy'}`}>{value}</p>
    </div>
  );
}

function statusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/-/g, ' ');
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  const color = tone === 'error' ? 'text-red-700' : 'text-neutral';
  return (
    <div className="p-8 text-center">
      <p className={`text-sm ${color}`}>{label}</p>
      {detail && <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{detail}</pre>}
    </div>
  );
}
